import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import heroMethod from "@/assets/hero-method.jpg";
import { VideoCardRow } from "@/components/video/VideoCardRow";
import type { VideoCardDisplay } from "@/components/video/types";
import { resolveBlogShareImageUrl } from "@/lib/blog-helpers";
import {
  getPracticeLibraryEntryBySlug,
  listPracticeLibraryItems,
  PracticeTopic,
  PracticeTopicLabels,
  type PracticeLibraryEntry,
} from "@/lib/practice-hub.functions";
import { practiceDrillJsonLd, pageHead } from "@/lib/seo";

const MarkdownContent = lazy(() =>
  import("@/components/blog/MarkdownContent").then((m) => ({ default: m.MarkdownContent })),
);

type PracticeDrill = Awaited<ReturnType<typeof getPracticeLibraryEntryBySlug>>;

function thumbnailHueFromId(id: string): number {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return hash;
}

function previewUrlFor(item: PracticeLibraryEntry): string | undefined {
  const resolved = resolveBlogShareImageUrl({
    shareImageUrl: item.preview_image_url,
    bodyMarkdown: item.body_markdown,
    defaultImageUrl: "",
  });
  return resolved || undefined;
}

function toVideoCardDisplay(item: PracticeLibraryEntry): VideoCardDisplay {
  return {
    id: item.id,
    title: item.title,
    excerpt: item.excerpt ?? "",
    thumbnailHue: thumbnailHueFromId(item.id),
    thumbnailUrl: previewUrlFor(item),
    badge: PracticeTopicLabels[item.topic as PracticeTopic] ?? "Library",
    to: "/practice/$slug",
    params: { slug: item.slug },
  };
}

export const Route = createFileRoute("/practice/$slug")({
  loader: async ({ params }) => {
    try {
      return await getPracticeLibraryEntryBySlug({ data: { slug: params.slug } });
    } catch {
      return null;
    }
  },
  head: ({ loaderData }) => {
    const entry = loaderData as unknown as PracticeDrill | null;
    const ogImage = entry
      ? resolveBlogShareImageUrl({
          shareImageUrl: entry.preview_image_url,
          bodyMarkdown: entry.body_markdown,
          defaultImageUrl: heroMethod,
        })
      : heroMethod;
    const title = `${entry?.title ?? "Practice drill"} | Mark Egly Golf`;
    const description =
      entry?.excerpt ?? "Golf practice drills from Mark Egly Golf in Des Moines, Iowa.";
    const path = `/practice/${entry?.slug ?? ""}`;

    return pageHead({
      title,
      description,
      path,
      image: ogImage,
      type: "article",
      jsonLd: entry
        ? practiceDrillJsonLd({
            title: entry.title,
            description,
            path,
            image: ogImage,
            bodyMarkdown: entry.body_markdown,
            publishedAt: entry.published_at,
            updatedAt: entry.updated_at,
          })
        : undefined,
    });
  },
  component: PracticeDrillPage,
});

function PracticeDrillPage() {
  const { slug } = Route.useParams();
  const loaderEntry = Route.useLoaderData();
  const entryQ = useQuery({
    queryKey: ["practice-drill", slug],
    queryFn: () => getPracticeLibraryEntryBySlug({ data: { slug } }),
    initialData: loaderEntry ?? undefined,
    enabled: !!slug,
  });
  const libraryQ = useQuery({
    queryKey: ["practice-library"],
    queryFn: () => listPracticeLibraryItems(),
  });

  const entry = entryQ.data;

  if (entryQ.isLoading) {
    return (
      <section className="container-page page-band-pad">
        <p className="text-foreground/60">Loading drill…</p>
      </section>
    );
  }

  if (!entry) {
    return (
      <section className="container-page page-band-pad">
        <Link
          to="/practice"
          className="inline-flex min-h-11 items-center gap-2 text-base text-ember hover:text-ember/80"
        >
          <ArrowLeft className="h-5 w-5 rtl:rotate-180" /> Back to Practice Hub
        </Link>
        <h1 className="mt-8 font-display text-4xl">Drill not found</h1>
        <p className="mt-3 text-foreground/60">
          This practice drill may have been removed or is not published.
        </p>
      </section>
    );
  }

  const topicLabel = PracticeTopicLabels[entry.topic as PracticeTopic] ?? entry.topic;
  const related = (libraryQ.data ?? [])
    .filter((item) => item.topic === entry.topic && item.id !== entry.id)
    .slice(0, 3)
    .map(toVideoCardDisplay);

  return (
    <>
      <section className="bg-ink">
        <div className="container-page page-hero-pad">
          <Link
            to="/practice"
            className="inline-flex min-h-11 items-center gap-2 text-base text-foreground/70 hover:text-ember"
          >
            <ArrowLeft className="h-5 w-5 rtl:rotate-180" /> Back to Practice Hub
          </Link>
          <div className="mt-5 font-display text-xs tracking-[0.18em] text-ember">{topicLabel}</div>
          <h1 className="mt-3 max-w-3xl font-display text-4xl leading-[0.95] md:text-6xl">
            {entry.title}
          </h1>
          {entry.excerpt ? (
            <p className="mt-4 max-w-2xl text-base text-foreground/75 md:text-lg">
              {entry.excerpt}
            </p>
          ) : null}
        </div>
      </section>

      <section className="container-page page-follow-pad">
        <div className="mx-auto w-full max-w-3xl md:max-w-4xl">
          <Suspense fallback={null}>
            <MarkdownContent content={entry.body_markdown} />
          </Suspense>
        </div>

        {related.length > 0 ? (
          <div className="mt-12">
            <VideoCardRow title={`More ${topicLabel} drills`} items={related} />
          </div>
        ) : null}

        <div className="mt-10 flex flex-col gap-4 border border-white/10 bg-black/40 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-2xl">Want help with this live?</h2>
            <p className="mt-1 text-sm text-foreground/70">
              Book a lesson or browse lesson formats.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/schedule" className="btn-ember btn-ember-hover min-h-11">
              Book a Lesson <ArrowRight className="h-5 w-5 rtl:rotate-180" />
            </Link>
            <Link
              to="/lessons"
              className="inline-flex min-h-11 items-center gap-2 border border-white/30 px-5 py-3 font-display text-sm tracking-[0.15em] hover:bg-white/5"
            >
              Lessons
            </Link>
          </div>
        </div>

        <div className="mt-8">
          <Link
            to="/practice"
            className="inline-flex min-h-11 items-center gap-2 text-base text-foreground/70 hover:text-ember"
          >
            <ArrowLeft className="h-5 w-5 rtl:rotate-180" /> Back to Practice Hub
          </Link>
        </div>
      </section>
    </>
  );
}
